/**
 * Activity Heatmap
 * Reusable hour×date CSS grid heatmap for extension and worker stats
 */

import { useMemo } from 'react';
import { Tooltip } from 'antd';

interface HeatmapDataPoint {
  hour: number;
  dayOfWeek: number;
  date: string;
  count: number;
}

interface ActivityHeatmapProps {
  data: HeatmapDataPoint[];
  color?: 'blue' | 'purple';
  isDark: boolean;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOUR_LABELS = [0, 3, 6, 9, 12, 15, 18, 21];

function getColorRgb(color: 'blue' | 'purple') {
  return color === 'blue' ? '22,119,255' : '114,46,209';
}

export function ActivityHeatmap({ data, color = 'blue', isDark }: ActivityHeatmapProps) {
  const { dates, matrix, maxCount, totalCount } = useMemo(() => {
    const mat = new Map<string, number>();
    let max = 0;
    let total = 0;
    const dateSet = new Set<string>();

    for (const d of data) {
      const key = `${d.date}_${d.hour}`;
      const val = (mat.get(key) || 0) + d.count;
      mat.set(key, val);
      if (val > max) max = val;
      total += d.count;
      dateSet.add(d.date);
    }

    const sortedDates = [...dateSet].sort();
    return { dates: sortedDates, matrix: mat, maxCount: max, totalCount: total };
  }, [data]);

  if (dates.length === 0) {
    return <div style={{ color: isDark ? '#8c8c8c' : '#595959', textAlign: 'center', padding: 24 }}>No activity data</div>;
  }

  const rgb = getColorRgb(color);
  const zeroColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const textDim = isDark ? '#8c8c8c' : '#595959';
  const weekendColor = '#fa8c16';

  function getCellColor(count: number): string {
    if (count === 0) return zeroColor;
    const intensity = maxCount > 0 ? count / maxCount : 0;
    const alpha = (0.06 + intensity * 0.85).toFixed(2);
    return `rgba(${rgb},${alpha})`;
  }

  function formatDateLabel(dateStr: string): { label: string; isWeekend: boolean } {
    const dt = new Date(dateStr + 'T12:00:00');
    const dow = dt.getDay();
    const month = dt.getMonth() + 1;
    const day = dt.getDate();
    return {
      label: `${month}/${day < 10 ? '0' + day : day} ${DAY_NAMES[dow]}`,
      isWeekend: dow === 0 || dow === 6,
    };
  }

  const legendAlphas = [0.04, 0.12, 0.28, 0.48, 0.7, 0.9];

  return (
    <div>
      <div style={{ maxHeight: 500, overflowY: 'auto', overflowX: 'auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '80px repeat(24, minmax(16px, 1fr))',
            gap: 2,
            minWidth: 620,
          }}
        >
          {/* Hour header */}
          <div />
          {Array.from({ length: 24 }, (_, h) => (
            <div
              key={`h-${h}`}
              style={{
                fontSize: 9,
                color: textDim,
                textAlign: 'center',
                lineHeight: '14px',
              }}
            >
              {HOUR_LABELS.includes(h) ? `${h}` : ''}
            </div>
          ))}

          {/* Date rows */}
          {dates.map((dateStr) => {
            const { label, isWeekend } = formatDateLabel(dateStr);
            return (
              <div key={dateStr} style={{ display: 'contents' }}>
                <div
                  style={{
                    fontSize: 10,
                    color: isWeekend ? weekendColor : textDim,
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </div>
                {Array.from({ length: 24 }, (_, h) => {
                  const count = matrix.get(`${dateStr}_${h}`) || 0;
                  return (
                    <Tooltip
                      key={`${dateStr}_${h}`}
                      title={`${dateStr} at ${h}:00 — ${count.toLocaleString()} clicks`}
                      mouseEnterDelay={0}
                      mouseLeaveDelay={0}
                    >
                      <div
                        style={{
                          height: 16,
                          borderRadius: 2,
                          background: getCellColor(count),
                          cursor: 'crosshair',
                          transition: 'transform 0.1s',
                        }}
                        onMouseEnter={(e) => {
                          (e.target as HTMLDivElement).style.transform = 'scale(1.3)';
                          (e.target as HTMLDivElement).style.zIndex = '2';
                        }}
                        onMouseLeave={(e) => {
                          (e.target as HTMLDivElement).style.transform = 'scale(1)';
                          (e.target as HTMLDivElement).style.zIndex = '0';
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 10,
          fontSize: 10,
          color: textDim,
        }}
      >
        <span>Less</span>
        <div style={{ display: 'flex', gap: 1 }}>
          {legendAlphas.map((a) => (
            <div
              key={a}
              style={{
                width: 16,
                height: 10,
                borderRadius: 2,
                background: `rgba(${rgb},${a})`,
              }}
            />
          ))}
        </div>
        <span>More</span>
        <span style={{ marginLeft: 12 }}>
          Total: {totalCount.toLocaleString()} clicks · Peak: {maxCount.toLocaleString()}/slot
        </span>
      </div>
    </div>
  );
}
